import { useState } from 'react';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, DataTable, ErrorBanner, Field, Flash, Input, Spinner, Tabs } from '../components/ui';
import { date, enumLabel, percent, pricePerShare, securityType, shares } from '../lib/format';
import { roleAtLeast } from '../lib/roles';

/**
 * The register itself.
 *
 * Three denominators are shown as explicit columns rather than a toggle, because
 * the interesting thing is usually the *difference* between them — a founder's
 * 60% outstanding and 45% fully diluted is one fact, not two views.
 */
export default function CapTable() {
  const { companyId, role } = useCompany();
  const [asOfDate, setAsOfDate] = useState('');
  const [view, setView] = useState('stakeholders');
  const [exportingFormat, setExportingFormat] = useState(null);
  const [lastExportFormat, setLastExportFormat] = useState('xlsx');
  const [exportError, setExportError] = useState(null);
  const [flash, setFlash] = useState(null);

  const table = useAsync(
    () => api.capTable(companyId, asOfDate ? { asOfDate } : undefined),
    [companyId, asOfDate]
  );

  async function exportAs(format) {
    setExportingFormat(format);
    setLastExportFormat(format);
    setExportError(null);
    try {
      await api.exportCapTable(companyId, format);
      setFlash(`Downloaded cap-table-export.${format}.`);
    } catch (err) {
      setExportError(err);
    } finally {
      setExportingFormat(null);
    }
  }

  if (table.loading) return <Spinner label="Deriving the cap table" />;
  if (table.error) return <ErrorBanner error={table.error} onRetry={table.reload} />;

  const data = table.data;
  const t = data.totals;
  // Bundles the stakeholder and securities sheets in full, which need LEGAL
  // to see unscoped — matches the backend's gate on GET /cap-table/export.
  const canExport = roleAtLeast(role, 'LEGAL');

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Cap table</h1>
          <p>
            As of {asOfDate ? date(asOfDate) : 'today'}
            {data.cached ? ' · served from cache' : ''}. A past date is reconstructed by
            folding the ledger up to that day, not by reading a snapshot.
          </p>
        </div>
        <div className="page-actions">
          <Field label="As of date">
            <Input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
          </Field>
          {asOfDate ? <Button onClick={() => setAsOfDate('')}>Today</Button> : null}
          <Button onClick={table.reload}>Refresh</Button>
          {canExport ? (
            <>
              <Button loading={exportingFormat === 'pdf'} onClick={() => exportAs('pdf')}>Export PDF</Button>
              <Button variant="primary" loading={exportingFormat === 'xlsx'} onClick={() => exportAs('xlsx')}>
                Export XLSX
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <Flash message={flash} onDone={() => setFlash(null)} />
      {exportError ? <ErrorBanner error={exportError} onRetry={() => exportAs(lastExportFormat)} /> : null}

      <Tabs
        active={view}
        onChange={setView}
        tabs={[
          { key: 'stakeholders', label: `Stakeholders (${data.stakeholders.length})` },
          { key: 'classes', label: `Share classes (${data.shareClasses.length})` },
          { key: 'plans', label: `Plans (${data.plans.length})` },
        ]}
      />

      {view === 'stakeholders' ? (
        <Card padded={false} title="Holdings by stakeholder">
          <DataTable
            rowKey={(r) => r.stakeholderId}
            empty="Nothing issued yet."
            columns={[
              {
                key: 'name',
                header: 'Stakeholder',
                render: (r) => (
                  <span className="primary-cell">
                    {r.name}{' '}
                    {r.kind === 'ENTITY' ? <Badge tone="neutral">entity</Badge> : null}
                  </span>
                ),
              },
              { key: 'outstanding', header: 'Outstanding', align: 'right', numeric: true, render: (r) => shares(r.outstanding) },
              { key: 'asConverted', header: 'As converted', align: 'right', numeric: true, render: (r) => shares(r.asConverted) },
              { key: 'fullyDiluted', header: 'Fully diluted', align: 'right', numeric: true, render: (r) => shares(r.fullyDiluted) },
              { key: 'pctOut', header: '% out', align: 'right', numeric: true, render: (r) => percent(r.percentOutstanding) },
              { key: 'pctFd', header: '% FD', align: 'right', numeric: true, render: (r) => percent(r.percentFullyDiluted) },
              {
                key: 'vested',
                header: 'Vested awards',
                align: 'right',
                numeric: true,
                render: (r) => (r.vestedAwards === '0' ? '—' : shares(r.vestedAwards)),
              },
            ]}
            rows={data.stakeholders}
          />
          <TotalsFooter totals={t} />
        </Card>
      ) : null}

      {view === 'classes' ? (
        <Card padded={false} title="By share class">
          <DataTable
            rowKey={(r) => r.shareClassId}
            columns={[
              { key: 'name', header: 'Class', render: (r) => <span className="primary-cell">{r.name}</span> },
              { key: 'classType', header: 'Type', render: (r) => <Badge tone="neutral">{enumLabel(r.classType)}</Badge> },
              { key: 'seniorityRank', header: 'Seniority', align: 'right', numeric: true },
              { key: 'authorized', header: 'Authorized', align: 'right', numeric: true, render: (r) => shares(r.authorized) },
              { key: 'outstanding', header: 'Outstanding', align: 'right', numeric: true, render: (r) => shares(r.outstanding) },
              { key: 'available', header: 'Available', align: 'right', numeric: true, render: (r) => shares(r.available) },
              { key: 'holders', header: 'Holders', align: 'right', numeric: true },
              { key: 'pct', header: '% FD', align: 'right', numeric: true, render: (r) => percent(r.percentFullyDiluted) },
            ]}
            rows={data.shareClasses}
          />
        </Card>
      ) : null}

      {view === 'plans' ? (
        <Card padded={false} title="Equity plans">
          <DataTable
            rowKey={(r) => r.planId}
            empty="No equity plans yet."
            columns={[
              { key: 'name', header: 'Plan', render: (r) => <span className="primary-cell">{r.name}</span> },
              { key: 'planType', header: 'Type' },
              { key: 'authorized', header: 'Authorized', align: 'right', numeric: true, render: (r) => shares(r.authorized) },
              { key: 'granted', header: 'Granted', align: 'right', numeric: true, render: (r) => shares(r.granted) },
              { key: 'unallocated', header: 'Unallocated', align: 'right', numeric: true, render: (r) => shares(r.unallocated) },
              {
                key: 'over',
                header: '',
                render: (r) => (r.overGranted ? <Badge tone="warning">over by {shares(r.overGranted)}</Badge> : null),
              },
            ]}
            rows={data.plans}
          />
        </Card>
      ) : null}

      <Card title="Holdings detail" padded={false}>
        <DataTable
          rowKey={(r) => r.securityId}
          empty="Nothing issued yet."
          columns={[
            { key: 'label', header: 'Certificate', render: (r) => <span className="primary-cell">{r.label}</span> },
            { key: 'holder', header: 'Holder', render: (r) => r.holderName },
            { key: 'securityType', header: 'Type', render: (r) => securityType(r.securityType) },
            { key: 'shareClass', header: 'Class', render: (r) => r.shareClass ?? '—' },
            { key: 'quantity', header: 'Quantity', align: 'right', numeric: true, render: (r) => shares(r.quantity) },
            { key: 'vested', header: 'Vested', align: 'right', numeric: true, render: (r) => shares(r.vested) },
            { key: 'strike', header: 'Strike', align: 'right', numeric: true, render: (r) => (r.strikePrice ? pricePerShare(r.strikePrice) : '—') },
            { key: 'issueDate', header: 'Issued', render: (r) => date(r.issueDate) },
          ]}
          rows={data.stakeholders.flatMap((s) =>
            s.holdings.map((h) => ({ ...h, holderName: s.name }))
          )}
        />
      </Card>

      <p className="muted" style={{ fontSize: 12 }}>
        {data.notes?.join(' ')}
      </p>
    </div>
  );
}

function TotalsFooter({ totals }) {
  return (
    <div className="totals-footer">
      <span>Totals</span>
      <dl>
        <div><dt>Outstanding</dt><dd className="num">{shares(totals.outstanding)}</dd></div>
        <div><dt>As converted</dt><dd className="num">{shares(totals.asConverted)}</dd></div>
        <div><dt>Fully diluted</dt><dd className="num">{shares(totals.fullyDiluted)}</dd></div>
      </dl>
    </div>
  );
}
